// src/features/hector-agent-casediscovery-v3/types/tools.types.ts

/**
 * Configuration des outils - Version unifiée V3
 *
 * Naming cohérent entre:
 * - la politique interne de l'agent
 * - WebSocketPool (frontend)
 * - WebSocket protocol (Backend)
 */

/**
 * Politique interne des outils de recherche envoyée au backend.
 *
 * Ces options ne sont plus exposées dans le composeur : elles décrivent les
 * capacités par défaut du tour et les exceptions de mode (par exemple Deep
 * Research), afin que la suppression des contrôles UI ne crée pas de valeur
 * implicite différente entre les hôtes du composeur.
 */
export interface ToolOptions {
  /** Recherche de jurisprudence activée par défaut. */
  jurisprudence: boolean;
  /** Recherche doctrinale activée par défaut. */
  doctrine: boolean;
  /** Contrôle les outils Légifrance. */
  legifrance: boolean;
}

/**
 * Controls how a host exposes the Case Discovery dossier context.
 *
 * `host_selectable` keeps a non-null human-selected dossier for each turn (the
 * lawyer can change the principal of a dock chat, not remove it). It is
 * deliberately distinct from `locked`: a host may suggest an initial dossier
 * without pinning every subsequent turn to it.
 */
export type CaseContextMode = 'selectable' | 'locked' | 'host_selectable';

/** Configuration envoyée au backend via WebSocket (snake_case) */
export interface ToolsConfig {
  // Recherche activée par la politique du tour
  search_jurisprudence: boolean;
  search_doctrine: boolean;
  /**
   * Réglage d'hôte lu par le serveur (#2597) : `false` retire `search_cases`,
   * `add_related_case` et `remove_related_case`. Seul le chat verrouillé sur son
   * dossier (numérotation des pièces) les refuse.
   */
  case_list_tools: boolean;
  /**
   * Réglage d'hôte lu par le serveur (#2610) : `true` offre `create_case`, le
   * dossier créé entrant dans la liste de la conversation. Refusé au seul chat
   * verrouillé sur son dossier, comme la gestion de la liste.
   */
  create_case: boolean;
  // Toujours actifs
  search_jp_by_id: boolean;
  read_attached_document: boolean;
  web_search: boolean;
  web_fetch: boolean;
  legifrance_search_code: boolean;
  legifrance_search_legislation: boolean;
  legifrance_get_article: boolean;
  legifrance_list_codes: boolean;
  legifrance_code_toc: boolean;
  /** Conventions collectives (fonds KALI) — même toggle « Légifrance » que ci-dessus. */
  legifrance_find_convention: boolean;
  legifrance_convention_toc: boolean;
  legifrance_search_convention: boolean;
  legifrance_get_convention_article: boolean;
  load_skill: boolean;
}

/**
 * Mapping de la politique interne vers le contrat WebSocket.
 *
 * Aucun outil de dossier ici : le serveur les offre d'après la liste des
 * dossiers de la conversation (spec #2592), pas d'après le dossier du composeur.
 *
 * @param options - Politique d'activation des outils pour le tour
 * @param caseContextMode - Seul le chat verrouillé sur son dossier refuse à
 * l'agent de chercher un dossier, de changer la liste et d'en créer un.
 */
export function mapToolOptionsToWS(
  options: ToolOptions,
  caseContextMode: CaseContextMode = 'selectable',
): ToolsConfig {
  return {
    // Politique de recherche du tour
    search_jurisprudence: options.jurisprudence,
    search_doctrine: options.doctrine,
    case_list_tools: caseContextMode !== 'locked',
    create_case: caseContextMode !== 'locked',
    // Toujours actifs
    search_jp_by_id: true,
    read_attached_document: true,
    web_search: true,
    web_fetch: true,
    legifrance_search_code: options.legifrance,
    legifrance_search_legislation: options.legifrance,
    legifrance_get_article: options.legifrance,
    legifrance_list_codes: options.legifrance,
    legifrance_code_toc: options.legifrance,
    legifrance_find_convention: options.legifrance,
    legifrance_convention_toc: options.legifrance,
    legifrance_search_convention: options.legifrance,
    legifrance_get_convention_article: options.legifrance,
    load_skill: true,
  };
}

export const DEFAULT_TOOL_OPTIONS: ToolOptions = {
  jurisprudence: true,
  doctrine: true,
  legifrance: true,
};
